import { apiClient } from "../client";
import type { PujaEnvelope } from "./puja";

/**
 * Kundli Studio history — the birth details behind every chart a signed-in
 * user casts. Only the INPUTS are stored; the studio re-casts the chart from
 * VedAstro when a saved kundli is opened. Every route needs a session.
 *
 * Envelope: { success, message, data, meta? } (same as the puja module).
 * Saving is idempotent — the same person/time/place again returns the existing
 * row (HTTP 200) with `last_opened_at` refreshed instead of a duplicate.
 */

export type KundliSource = "website" | "seeker_dashboard" | "astrologer_dashboard" | "app";

export interface SavedKundli {
  id: number;
  booking_id: number | null;
  /** "" when the chart was cast unnamed. */
  name: string;
  gender: "male" | "female" | "other" | null;
  /** "YYYY-MM-DD" */
  birth_date: string;
  /** "HH:MM" (24h) */
  birth_time: string;
  place_name: string;
  latitude: number;
  longitude: number;
  /** e.g. "+05:30" */
  tz_offset: string;
  label: string | null;
  notes: string | null;
  source: KundliSource;
  last_opened_at: string;
  created_at: string;
  updated_at: string;
}

export interface SaveKundliPayload {
  name?: string;
  gender?: string;
  birth_date: string;
  birth_time: string;
  place_name: string;
  latitude: number;
  longitude: number;
  tz_offset?: string;
  source?: KundliSource;
  booking_id?: number;
  label?: string;
  notes?: string;
}

/** Every field optional; `source` and `booking_id` are not editable. */
export type UpdateKundliPayload = Partial<Omit<SaveKundliPayload, "source" | "booking_id">>;

export type KundliSort ="last_opened" | "created" | "name" | "birth_date";

/** Every filter is optional and they combine; an unknown value answers 422. */
export interface KundliListParams {
  page?: number;
  limit?: number;
  /** Free text over name, label and place. */
  search?: string;
  gender?: "male" | "female" | "other";
  source?: KundliSource;
  /** "YYYY-MM-DD", inclusive. */
  birth_date_from?: string;
  /** "YYYY-MM-DD", inclusive. */
  birth_date_to?: string;
  /** Default "last_opened". */
  sort?: KundliSort;
  /** Default asc for sort=name, desc otherwise. */
  order?: "asc" | "desc";
}

export const kundlis = {
  /** GET /kundlis — most recently opened first unless `sort` says otherwise; paginated. */
  list: (params: KundliListParams = {}) =>
    apiClient.get<PujaEnvelope<SavedKundli[]>>("/kundlis", { params: { ...params } }),

  /** POST /kundlis — save (or touch) a cast chart. */
  save: (payload: SaveKundliPayload) => apiClient.post<PujaEnvelope<SavedKundli>>("/kundlis", payload),

  /** GET /kundlis/{id} — owner-scoped (404 otherwise). */
  get: (id: number) => apiClient.get<PujaEnvelope<SavedKundli>>(`/kundlis/${id}`),

  /**
   * PATCH /kundlis/{id} — partial edit, birth details included (same id kept).
   * latitude + longitude go together; 409 when the result duplicates another
   * saved kundli.
   */
  update: (id: number, payload: UpdateKundliPayload) =>
    apiClient.patch<PujaEnvelope<SavedKundli>>(`/kundlis/${id}`, payload),

  /** DELETE /kundlis/{id} */
  remove: (id: number) => apiClient.delete<PujaEnvelope<null>>(`/kundlis/${id}`),
};
